import { CustomViewStatisticProvider } from '@lighthouse/block'
import type { DependentDataContextValue } from '@lighthouse/shared'
import {
    DependentDataProvider,
    getCustomStatisticViewFields,
    getViewCustomStatisticConfig,
    useDependentDataContext,
    VIEW_BLOCK_NAME_MAP
} from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useCustomViewStatistic } from '@/hooks/useCustomViewStatistic'
import { useDataSource } from '@/hooks/useDataSource'

import CustomCommentController from '../CustomCommentController'
import CustomMonthCalendarController from '../CustomMonthCalendarController'
import CustomMultiDimensionalController from '../CustomMultiDimensionalController'
import CustomTreeController from '../CustomTreeController'
import CustomWaterfallAndGridController from '../CustomWaterfallAndGridController'
import CustomWeekCalendarController from '../CustomWeekCalendarController'
import type { CustomControllerProps } from '../types'

const CustomController: React.FC<CustomControllerProps> = props => {
    const { blockData } = props
    const {
        config: { viewType, breakPoint, pointer }
    } = blockData
    const { viewLayout } = breakPoint
    const layout = viewLayout?.layout
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const parentDependentData = useDependentDataContext()

    // const userId = useAtomData(appRoleAtom)
    const dataSource = useDataSource(appId, envId, pointer)
    const customStatisticValues = useCustomViewStatistic({ blockData })
    const extraFields = useMemo(
        () => getCustomStatisticViewFields(getViewCustomStatisticConfig(blockData.config), pointer),
        [blockData.config, pointer]
    )

    const dependentData: DependentDataContextValue = useMemo(
        () => ({
            ...parentDependentData,
            customViewData: {
                dataSource,
                name: VIEW_BLOCK_NAME_MAP['custom'],
                extraFields
            }
        }),
        [dataSource, extraFields, parentDependentData]
    )

    const customView = useMemo(() => {
        if (viewType !== 'custom') {
            return null
        }

        switch (layout) {
            case 'weekCalendar': {
                return <CustomWeekCalendarController {...props} />
            }
            case 'monthCalendar': {
                return <CustomMonthCalendarController {...props} />
            }
            case 'comment': {
                return <CustomCommentController {...props} />
            }
            case 'tree': {
                return <CustomTreeController {...props} />
            }
            case 'multiDimensional': {
                return <CustomMultiDimensionalController {...props} />
            }
            case 'carousel': {
                return <CustomWaterfallAndGridController {...props} />
            }
            default: {
                return <CustomWaterfallAndGridController {...props} />
            }
        }
    }, [layout, props, viewType])

    return (
        <DependentDataProvider value={dependentData}>
            <CustomViewStatisticProvider value={customStatisticValues}>{customView}</CustomViewStatisticProvider>
        </DependentDataProvider>
    )
}

export default CustomController
